import { GeoIPInfo } from '../geo-ip-info';

export interface LocaleSettings {
  lastKnownIpInfo?: GeoIPInfo;
  localeName?: string;
  decimalSeparator?: string;
  decimalSeparatorOverride?: '.' | ',' | '' /** NOTE: '' means "automatic" */;
  thousandsSeparator?: string /** GeoIP-derived grouping separator (#4204) */;
  thousandsSeparatorOverride?: '.' | ',' | ' ' | '' /** explicit override; '' means "automatic". #4204 */;
  clock24hr?: boolean;
  weekStartDay?: number; // 1=Monday, 6=Saturday, 7=Sunday (ISO weekday, but Sunday=7 not 0)
  lastCheck?: string /** ISO time */;
  lastVersion?: number; // used to reinitialize when we add new field to locale settings
}

// returns the user's effective decimal separator: explicit override if set, else locale default, else '.'
export function getEffectiveDecimalSeparator(locale: LocaleSettings | undefined): string {
  return locale?.decimalSeparatorOverride || locale?.decimalSeparator || '.';
}

// Returns the user's effective THOUSANDS (grouping) separator, mirroring the
// decimal-separator precedence: explicit override, else the GeoIP-derived value,
// else derive it from the decimal separator. In every case the result is
// guaranteed to DIFFER from the decimal separator — a grouping char equal to the
// decimal point would make the number ambiguous ("1.234.5"), so a colliding
// explicit value is ignored in favour of the derived one. See #4204.
export function getEffectiveThousandsSeparator(locale: LocaleSettings | undefined): string {
  const decimal = getEffectiveDecimalSeparator(locale);
  const explicit = locale?.thousandsSeparatorOverride || locale?.thousandsSeparator;
  if (explicit && explicit !== decimal) {
    return explicit;
  }
  // no usable configured grouping: derive from the decimal separator so they differ
  return decimal === ',' ? '.' : ',';
}

// The characters that can appear as a grouping separator across our locales:
// dot, comma, and the (narrow) no-break spaces some runtimes emit for fr/nl.
const GROUPING_CHARS = ['.', ',', ' ', ' ', ' '];

// True when `str` is a plain integer written with `sep` as a thousands grouping
// separator on strict 3-digit boundaries (e.g. "12,345" / "1 234 567"). This
// distinguishes a grouped integer from a decimal that happens to use the same
// character: "3,14" is NOT a grouping pattern (",14" is not a full group). #4204
function isThousandsGroupedInteger(str: string, sep: string): boolean {
  const body = str.startsWith('-') ? str.slice(1) : str;
  const escaped = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^\\d{1,3}(${escaped}\\d{3})+$`).test(body);
}

export function parseLocaleFloat(str: string, locale: LocaleSettings): number {
  let separator = getEffectiveDecimalSeparator(locale);
  const hasDot = str.includes('.');
  const hasComma = str.includes(',');

  if (hasDot && hasComma) {
    // A grouped value has both a '.' and a ',': the LAST of the two is the
    // decimal point, the other (plus any spaces) is grouping. So "1.234,5" (es)
    // reads ',' as decimal and "12,345.6" (en) reads '.' as decimal. We do NOT
    // change the stored override from a grouped value. See #4204.
    separator = str.lastIndexOf('.') > str.lastIndexOf(',') ? '.' : ',';
  } else {
    // exactly one (or no) separator present.
    const present = hasDot ? '.' : hasComma ? ',' : '';
    const thousands = getEffectiveThousandsSeparator(locale);
    if (present && present === thousands && present !== separator && isThousandsGroupedInteger(str, present)) {
      // a lone separator that IS the grouping char and falls on strict 3-digit
      // boundaries is thousands grouping, not a decimal — "12,345" (en) is
      // 12345. Leave `separator` as the effective decimal so the strip below
      // removes the grouping, and do NOT flip the stored decimal preference.
    } else if (separator !== '.' && hasDot && !hasComma) {
      // otherwise keep the original auto-detect that flips the stored preference
      // when the user clearly typed the other decimal separator.
      separator = locale.decimalSeparatorOverride = '.';
    } else if (separator !== ',' && hasComma && !hasDot) {
      separator = locale.decimalSeparatorOverride = ',';
    }
  }

  // strip every grouping character except the decimal separator, so a grouped
  // value produced by formatLocaleFloat round-trips back to its number. #4204
  for (const g of GROUPING_CHARS) {
    if (g !== separator) {
      str = str.split(g).join('');
    }
  }

  str = str.replace(separator, '.'); // this covers eventual even weirder separators
  return parseFloat(str);
}

export function formatLocaleFloat(f: number, locale: LocaleSettings, forceDecimals?: boolean, group: boolean = true): string {
  let str = f.toString();
  if (forceDecimals) {
    if (str.indexOf('.') < 0) {
      str += '.0';
    }
  }
  // Split the canonical "-1234.5" into sign / integer / fraction, group the
  // integer's thousands (for display), then join with the locale decimal
  // separator. We build it in one pass (not two string replaces) because the
  // grouping separator can itself be '.', which a naive replace of the decimal
  // point would corrupt. `group` is OFF for editable-input echoes (a grouped
  // "1,234" typed back would be misread as 1.234) and for machine/export use;
  // those callers pass group=false or use formatNumberMachine. #4204.
  const negative = str.startsWith('-');
  if (negative) {
    str = str.slice(1);
  }
  const dot = str.indexOf('.');
  let intPart = dot < 0 ? str : str.slice(0, dot);
  const fracPart = dot < 0 ? '' : str.slice(dot + 1);
  if (group) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, getEffectiveThousandsSeparator(locale));
  }
  const fraction = fracPart !== '' ? getEffectiveDecimalSeparator(locale) + fracPart : '';
  return (negative ? '-' : '') + intPart + fraction;
}

export function formatNumberMachine(f: number, opts?: { decimals?: number }): string {
  // Locale-INDEPENDENT number formatting for machine / export / round-trip use:
  // always a plain '.' decimal and NO thousands grouping. This is the number
  // counterpart of date-format-machine.ts. Use it for CSV, data keys, and
  // live-input echoes — never for user-facing display of a value. #4204.
  return opts?.decimals != null ? f.toFixed(opts.decimals) : f.toString();
}

/** If denominator is bigger than maxDenominator, null is returned. Users don't need to see ugly fractions, format otherwise in that case.
 *
 * If `opts.makeBallpark` then round to a human-readable form. All other options are ignored and the output is a fraction that humans will not consider silly.
 * E.g instead of 2/7 show 1/4, instead of 10 3/8 show just 10.
 * In ballpark mode, if the number is very small, `null` is returned signaling that there shouldn't be an estimation because this needs precise measurement. */
export function toFraction(ieee: number, opts?: { maxDenominator?: number; precision?: number; makeBallpark?: boolean }): string | null {
  let numerator = 1.0;
  let denominator = 1.0;
  const precision = (opts && opts.precision) || Number.EPSILON * 100; /* we never need exact precision, so stay on the safe side */
  const maxDenominator = opts && opts.maxDenominator;
  const makeBallpark = opts && opts.makeBallpark;

  let isNegative = ieee < 0.0;
  if (isNegative) {
    //make it positive so while loop works
    ieee = -ieee;
  }

  let whole = Math.floor(ieee);
  ieee -= whole;

  if (ieee < precision) {
    return (isNegative ? '-' : '') + whole.toString();
  }

  if (makeBallpark) {
    let bestDenominator = 0;
    let bestNumerator = 1;
    let bestError = ieee;
    let accepted: number[][] = [
      [1, 2, 5],
      [1, 3, 1],
      [2, 3, 1],
      [1, 4, 3],
      [3, 4, 3],
      [1, 1, Number.MAX_SAFE_INTEGER],
    ];
    for (let [tryNumerator, tryDenominator, upTo] of accepted) {
      if (whole > upTo) {
        continue;
      }
      let tryError = Math.abs(tryNumerator / tryDenominator - ieee);
      if (tryError < bestError) {
        bestDenominator = tryDenominator;
        bestNumerator = tryNumerator;
        bestError = tryError;
      }
    }
    if (bestDenominator === 0) {
      if (whole === 0) {
        return null;
      }
      return (isNegative ? '-' : '') + whole.toString();
    } else if (Math.abs(bestNumerator / bestDenominator - 1) < precision) {
      return (isNegative ? '-' : '') + (whole + 1).toString();
    }
    denominator = bestDenominator;
    numerator = bestNumerator;
  } else {
    let count = 0;
    while (Math.abs(numerator / denominator - ieee) > precision) {
      if (count++ > 1000) {
        // this should never happen, but just in case
        return ieee.toString();
      }
      if (numerator / denominator < ieee) {
        numerator++;
        denominator--;
      } else if (numerator / denominator > ieee) {
        denominator++;
      }
      if (maxDenominator !== undefined && denominator > maxDenominator) {
        return null;
      }
    }
  }

  return (isNegative ? '-' : '') + (whole > 0 ? whole.toString() + ' ' : '') + numerator.toString() + '/' + denominator.toString();
}

export function parseFraction(str: string, locale: LocaleSettings): number {
  let [, signStr, wholeStr, nomStr, denumStr] = str.match(/^\s*(\-)?\s*(\d+\s+)?(\d+)\/(\d+)\s*$/) || [, , , , ,];
  if (!nomStr || !denumStr) {
    return parseLocaleFloat(str, locale);
  }

  let sign = signStr === '-' ? -1 : +1;
  let whole = wholeStr ? parseInt(wholeStr) : 0;
  let nom = parseInt(nomStr);
  let denum = parseInt(denumStr);

  if (!isFinite(whole) || !isFinite(nom) || !isFinite(denum)) {
    return parseLocaleFloat(str, locale);
  }

  if (denum === 0) {
    if (nom === 0) {
      return NaN;
    } else {
      return Infinity * sign;
    }
  }

  return sign * (whole + nom / denum);
}

export interface ParsedPrice {
  currencyPre: string;
  value: number;
  thousands: string;
  decimal: string;
  currencyPost: string;
}

/**
 * Parse a human readable price that might contain prefix and/or postfix, and has potential decimal and thousands separator.
 *
 * NOTE: This can handle prices only up to 999,999.99 units!
 * It will return `undefined` if it cannot parse (seemingly) correctly.
 */
export function parsePrice(str: string | undefined): ParsedPrice | undefined {
  if (!str) {
    return undefined;
  }

  let [matched, currencyPre, valueThousands, thousands, valueOnes, decimal, valueDecimal, currencyPost] = str.match(
    /^([^0-9]*)([0-9]{0,3}?)([,.]?)([0-9]+)([,.]?)([0-9]*)([^0-9]*)$/,
  ) || [null, '', '', '', '', '', '', ''];
  if (!matched) {
    return undefined;
  }
  return {
    value: parseFloat(valueThousands + valueOnes + '.' + valueDecimal),
    thousands,
    decimal,
    currencyPre: currencyPre,
    currencyPost: currencyPost,
  };
}
